/**
 * Commandes - Liste des commandes pour les prestataires
 * 
 * Version refactorisée utilisant les modules:
 * - types.ts: Interfaces et types
 * - statusConfig.ts: Configuration des onglets et statuts
 * - utils.ts: Fonctions utilitaires
 */
import './Commandes.css';

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// Icons importés comme composants SVG
let PlusIcon: any = null;
let ExportIcon: any = null;
let FilterIcon: any = null;
let DeleteIcon: any = null;
let CopyIcon: any = null;
let CheckIcon: any = null;
try {
  PlusIcon = require('../../assets/icons/plus.svg').ReactComponent;
  ExportIcon = require('../../assets/icons/export.svg').ReactComponent;
  FilterIcon = require('../../assets/icons/filter.svg').ReactComponent;
  // Corbeille : meme icone que la suppression d'un brouillon d'operation acheteur.
  DeleteIcon = require('../../assets/icons/delete.svg').ReactComponent;
  // Copie du code (colonne Code) : duplicate.svg est l'icone "copier" du jeu existant, check.svg
  // sert d'accuse de reception temporaire (2s, cf. handleCopyCode).
  CopyIcon = require('../../assets/icons/duplicate.svg').ReactComponent;
  CheckIcon = require('../../assets/icons/check.svg').ReactComponent;
} catch (e) {
  // Icons fallback si non disponibles
}
import { useAuth } from "../../contexts/AuthContext";
import { isClientRole, isAdminRole } from '../../utils/roleUtils';
import { prestaRowTabKind } from '../../utils/commandeRowTab';
import { libelleStatut } from '../../utils/statusLabel';
import { fetchUnreadCountsMap } from '../../services/messagerie/messagerieUnread';
import { useNotifications } from "../../contexts/NotificationsContextType";
import { handleExportCSV, handlePrint } from '../../utils/commonHandlers';
import CompteARebourd from '../../composants/CompteARebourd/CompteARebourd';
import SearchBar from '../../composants/SearchBar/SearchBar';
import TableSkeleton from '../../composants/Skeleton/TableSkeleton';
import Button from '../../composants/Button/Button';
import TableauDeBord from '../../composants/TableauDeBord/TableauDeBord';
import TabNav from '../../composants/TabNav/TabNav';
import FiltreDeCommande from '../../composants/FiltreDeCommande/FiltreDeCommande';
import LancerMarcheModal from '../../composants/LancerMarcheModal/LancerMarcheModal';
import ValidationPopUp from '../../composants/ValidationPopUp/ValidationPopUp';
import { formatDate, formatPrice } from '../../utils/entityUtils';

// Imports depuis les modules refactorisés
import { Commande, ApiResponse, PaginationState } from './types';
import { TABS, TAB_TO_STATUS_MAPPING, TYPE_OPTIONS } from './statusConfig';
import {
  toCssClass,
  getUserRoleFromStorage,
  getStatusActionMapping,
  transformCommandesData,
  sortPaniersImportesByPriority
} from './utils';
import {
  FilterOptions,
  transformFiltersToApiParams,
  buildQueryString,
  mergeFiltersWithBaseParams
} from './api';

// Feature "chiffrage" : service (routes core /bdc/chiffrage*), types partagés, catalogue (résolution
// des prix à la volée) et devis PDF. Fondations existantes, importées telles quelles.
import { chiffrageService } from '../../services/chiffrageService';
import ArticlesService, { Article } from '../../services/articlesService';
import ChiffragePDF, { ChiffragePDFArticle, ChiffragePDFEntete } from '../PDF/ChiffragePDF';
import ChiffrageCreateModal from '../Chiffrage/components/ChiffrageCreateModal';
import type { Chiffrage, ChiffrageCouple } from '../Chiffrage/chiffrageTypes';

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

function Commandes({ onNotificationUpdate }: { onNotificationUpdate?: (count: number) => void }) {
  const { user } = useAuth();
  const isClientView = isClientRole(user?.role || '');
  const navigate = useNavigate();
  const location = useLocation();

  // États — initialisation directe depuis location.state pour éviter le flash de scroll
  // `openChiffragesTab` : retour depuis la page /chiffrage ("Tous les chiffrages") -> on atterrit
  // directement sur l'onglet Chiffrages, pas sur "Tous".
  const _navState = location.state as { openBdcModal?: boolean; openChiffragesTab?: boolean; initialValues?: { lotTechnique?: string; lotGeographique?: string } } | null;
  const [isLancerMarcheModalOpen, setIsLancerMarcheModalOpen] = useState(false);
  const [isBonCommandeModalOpen, setIsBonCommandeModalOpen] = useState(() => !!_navState?.openBdcModal);
  const [bdcInitialValues, setBdcInitialValues] = useState<{ lotTechnique?: string; lotGeographique?: string } | undefined>(() => _navState?.openBdcModal ? _navState.initialValues : undefined);

  // Nettoyer le state de navigation
  useEffect(() => {
    if (_navState?.openBdcModal) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [commandes, setCommandes] = useState<Commande[]>([]);
  const [filteredData, setFilteredData] = useState<Commande[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  // Terme de la barre de recherche. C'est un ETAT, pas un fetch a part : il doit se
  // combiner a l'onglet actif, au filtre de type et a la pagination. L'ancien
  // handleSearch appelait l'API en direct et perdait ces trois dimensions.
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [unreadCommandes, setUnreadLocalCommandes] = useState<number>(0);
  const [dataArchive, setDataArchive] = useState<[]>([]);
  const [paniersImportesCount, setPaniersImportesCount] = useState<number>(0);
  const [actionRequisCount, setActionRequisCount] = useState<number>(0);
  // Pastilles messagerie, keyees par `${source}:${operationId}` (MS et BDC : bases distinctes,
  // ids d'operation potentiellement en collision -> cle composite).
  const [unreadMsgCounts, setUnreadMsgCounts] = useState<Record<string, { questions_reponses: number; messagerie: number }>>({});
  const abortControllerRef = useRef<AbortController | null>(null);

  // États pour la suppression de BCI
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [commandeToDelete, setCommandeToDelete] = useState<Commande | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Contexte de notifications
  const { setUnreadCommandes } = useNotifications();

  // Onglet actif - 1 = 'Action requise' par défaut
  const [activeTab, setActiveTab] = useState(() =>
    _navState?.openChiffragesTab ? TABS.indexOf('Chiffrages') : 0,
  );
  const [selectedType, setSelectedType] = useState<string>(TYPE_OPTIONS[0].value);

  // État de pagination
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    lastPage: 1,
    perPage: 100,
    total: 0
  });

  // État des filtres actifs
  const [activeFilters, setActiveFilters] = useState<Partial<FilterOptions>>({});

  // États de l'onglet "Chiffrage" (liste des chiffrages du prestataire + couples titulaires servant
  // à enrichir l'affichage et à résoudre les prix du devis). Plus de filtre par statut : l'état d'un
  // chiffrage se déduit du code (voir colonne "État"). `copiedCode` : feedback transitoire du bouton
  // "Copier le code".
  const [chiffrages, setChiffrages] = useState<Chiffrage[]>([]);
  const [chiffrageCouples, setChiffrageCouples] = useState<ChiffrageCouple[]>([]);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  // Modale "Nouveau chiffrage" : elle s'ouvre ICI (page Commandes). La redirection vers /chiffrage
  // n'a lieu qu'a la validation (nom + lots), avec la selection portee par l'URL.
  const [createChiffrageModalOpen, setCreateChiffrageModalOpen] = useState(false);
  // Prix total par chiffrage, resolu a la volee (aucun prix n'est stocke) : cle = id du chiffrage.
  const [chiffrageTotals, setChiffrageTotals] = useState<Map<number, number>>(new Map());
  // Suppression d'un chiffrage : double confirmation par modale (meme patron que les brouillons
  // d'operation acheteur).
  const [chiffrageToDelete, setChiffrageToDelete] = useState<Chiffrage | null>(null);
  const [isDeletingChiffrage, setIsDeletingChiffrage] = useState(false);
  const [deleteChiffrageError, setDeleteChiffrageError] = useState<string | null>(null);

  // ============================================================================
  // HANDLERS - ONGLETS ET TYPE
  // ============================================================================

  const handleOpenLancerMarcheModal = useCallback(() => {
    setIsLancerMarcheModalOpen(true);
  }, []);

  const handleTabChange = useCallback((tabIndex: number) => {
    if (tabIndex === activeTab) return;

    // Réinitialiser les états - le useEffect déclenchera le fetch
    setLoading(true);
    setError(null);
    setCommandes([]);
    setFilteredData([]);
    setDataArchive([]);
    setCurrentPage(1);
    setActiveTab(tabIndex);
    // Réinitialiser les filtres lors du changement d'onglet
    setActiveFilters({});
  }, [activeTab]);

  const handleTypeChange = useCallback((value: string) => {
    setSelectedType(value);
    // Le useEffect déclenchera le fetch grâce à la dépendance selectedType
  }, []);

  // ============================================================================
  // HANDLERS - NAVIGATION
  // ============================================================================

  const handleRowClick = useCallback((id: string) => {
    // Onglet Chiffrages : la ligne ouvre directement le chiffrage concerne (d'ou l'absence de bouton
    // "Voir"). Ce court-circuit est indispensable : la suite cherche la ligne dans `commandes`, ou un
    // chiffrage n'existe pas -> le clic ne faisait rien.
    if (TABS[activeTab] === 'Chiffrages') {
      navigate(`/chiffrage?id=${id}`);
      return;
    }

    const commande = commandes.find(c => c.id === id);
    if (!commande) return;

    const source = commande.source;
    const statusId = commande.status.id;
    const basePath =
      source?.toLowerCase().trim() === 'bc'
        ? '/commande-bc'
        : source?.toLowerCase().trim() === 'bci'
          ? '/commande-bci'
          : '/commande-ms';

    // BCI : comportement historique conserve (le routing par kind ne couvre que BC/MS). L'action
    // portait 'valider' au statut 11, 'view' sinon ; l'onglet restait 0.
    if (source === 'BCI') {
      const action = statusId === 11 ? 'valider' : 'view';
      navigate(`${basePath}/${id}?tab=0`, { state: { action } });
      return;
    }

    // BC/MS : destination par KIND d'onglet (resolu par useCommandeTabs / useCommandeMsTabs), et non
    // par index en dur qui se trompait de cible. La regle metier PRESTA vit dans commandeRowTab :
    // le presta chiffre/repond dans son onglet Offre (BC) ou Candidature (MS), consulte le bon de
    // commande signe dans Notification. Fallback historique si statut inconnu.
    const kind = prestaRowTabKind(source, statusId) ?? 'historique';
    navigate(`${basePath}/${id}?tab=${kind}`, { state: { action: '' } });
  }, [commandes, navigate, activeTab]);

  // Actions spécifiques - mémoisées pour éviter les re-renders
  const handleCandidater = useCallback((id: string) => navigate(`/commande-ms/${id}?tab=1`, { state: { action: 'candidater' } }), [navigate]);
  const handleAnalyser = useCallback((id: string) => navigate(`/commande-ms/${id}?tab=2`, { state: { action: 'analyser' } }), [navigate]);
  const handleReprendre = useCallback((id: string) => navigate(`/commande-ms/${id}?tab=1`, { state: { action: 'reprendre' } }), [navigate]);
  const handleSigner = useCallback((id: string) => navigate(`/commande-ms/${id}?tab=2`, { state: { action: 'signer' } }), [navigate]);
  const handleAccepter = useCallback((id: string) => navigate(`/commande-bc/${id}?tab=2`, { state: { action: 'accepter' } }), [navigate]);
  const handleChiffrer = useCallback((id: string) => navigate(`/commande-bc/${id}?tab=2`, { state: { action: 'chiffrer' } }), [navigate]);

  const handleValider = useCallback((row: Commande) => {
    if (!row.id || row.source !== 'BCI') return;
    navigate(`/commande-bci/${row.id}?tab=0`, { state: { action: 'valider' } });
  }, [navigate]);

  const handleExport = useCallback((id: string) => {
    const commandeToExport = commandes.find(cmd => cmd.id.toString() === id);
    if (commandeToExport) {
      // Note: columns sera défini au moment de l'exécution, pas besoin dans les dépendances
      handleExportCSV([commandeToExport], [], `commande-${id}-${new Date().toISOString().slice(0, 10)}.csv`);
    }
  }, [commandes]);

  // ============================================================================
  // HANDLERS - TÉLÉCHARGEMENTS
  // ============================================================================

  const handleDownload = useCallback(async (commande: Commande) => {
    try {
      // Formater le département sur 2 chiffres (ex: 6 -> "06")
      const departement = commande?.id_lot_departement?.toString().padStart(2, '0') || '';

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/documents/signed-contrat`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'Accept': 'application/pdf'
          },
          body: JSON.stringify({
            external_id: commande?.source === 'BCI'
              ? `${departement}${commande?.operation.numero?.toString() || commande?.operation?.id?.toString()}F`
              : commande?.operation.numero?.toString() || commande?.operation?.id?.toString(),
            external_type: "operation",
            source: commande?.source || 'MS',
          })
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement du document signé');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const annee = process.env.REACT_APP_ANNEE_EDITION || "25";
      const numeroSansP = commande?.operation?.numero;
      a.download = `${annee}${numeroSansP} (signed).pdf`;

      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue lors du téléchargement du marché signé');
    }
  }, []);

  const downloadArchivePDF = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/bdc-archive/${id}/download`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erreur téléchargement archive');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `archive-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Impossible de télécharger le fichier PDF.');
    }
  };

  const handleExportAll = async () => {
    setIsExporting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/commandes/exportCommandes`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) throw new Error('Erreur lors de l\'exportation des commandes');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `export-commandes-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      alert('Une erreur est survenue lors de l\'export des commandes');
    } finally {
      setIsExporting(false);
    }
  };

  const handleRelancerSignataire = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/operation/${id}/relancer-signataire`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Erreur lors de la relance');

      alert('Relance envoyée avec succès');
    } catch (error) {
      console.error('Erreur lors de la relance:', error);
      alert('Une erreur est survenue lors de l\'envoi de la relance');
    }
  };

  // ============================================================================
  // HANDLERS - SUPPRESSION BCI
  // ============================================================================

  /**
   * Ouvre la popup de confirmation pour supprimer une commande BCI
   */
  const handleDeleteBCI = useCallback((commande: Commande) => {
    setCommandeToDelete(commande);
    setDeleteError(null);
    setIsDeleteModalOpen(true);
  }, []);

  /**
   * Confirme la suppression de la commande BCI
   */
  const handleConfirmDelete = async () => {
    if (!commandeToDelete || isDeleting) {
      setIsDeleteModalOpen(false);
      return;
    }

    const commandeId = commandeToDelete.id.toString();
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bdci/commande/${commandeId}/delete`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de la suppression de la commande');
      }

      // Retirer la commande de la liste locale
      setCommandes(prev => prev.filter(cmd => cmd.id.toString() !== commandeId));
      setFilteredData(prev => prev.filter(cmd => cmd.id.toString() !== commandeId));

      // Mettre à jour le total de la pagination
      setPagination(prev => ({ ...prev, total: prev.total - 1 }));

      // Fermer la popup
      setIsDeleteModalOpen(false);
      setCommandeToDelete(null);
      setIsDeleting(false);
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      setDeleteError(error.message || 'Une erreur est survenue lors de la suppression de la commande.');
      setIsDeleting(false);
    }
  };

  /**
   * Annule la suppression de la commande BCI
   */
  const handleCancelDelete = useCallback(() => {
    setIsDeleteModalOpen(false);
    setCommandeToDelete(null);
    setDeleteError(null);
  }, []);

  // ============================================================================
  // HANDLERS - PAGINATION, FILTRES, RECHERCHE
  // ============================================================================

  const handlePageChange = useCallback((page: number) => {
    // Mettre à jour currentPage - le useEffect déclenchera le fetch
    setCurrentPage(page);
  }, []);

  const handleFilter = useCallback(() => {
    setIsFilterModalOpen(true);
  }, []);

  /**
   * Barre de recherche.
   *
   * Elle ne déclenche plus d'appel API à part : elle pose le terme dans l'état et
   * laisse l'effet principal recharger l'onglet courant. L'ancienne version tapait
   * `/commandes?commande_nom=...` en direct, ce qui avait trois défauts : elle
   * ignorait l'onglet et le filtre de type actifs, elle cassait la pagination (page
   * figée à 1 avec un total non paginable), et elle ne cherchait que dans le libellé
   * de la commande — un bon de commande n'était donc pas trouvable par son numéro,
   * qui vit sur l'opération. Le paramètre `search` couvre les deux.
   */
  const handleSearch = useCallback((query: string) => {
    const trimmed = query.trim();

    if (trimmed === searchQuery) {
      // Même terme resoumis : rien à recharger, on ne laisse pas tourner le spinner.
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    setCurrentPage(1);
    setSearchQuery(trimmed);
  }, [searchQuery]);

  const handleSelectionChange = useCallback((selectedIds: string[]) => {
    setSelectedItems(selectedIds);
  }, []);

  /**
   * Vérifie si des filtres sont actuellement actifs
   */
  const hasActiveFilters = useMemo(() => {
    const hasLotTechnique = !!activeFilters.lotTechnique;
    const hasLotGeographique = !!activeFilters.lotGeographique;
    const hasNomOperation = !!activeFilters.nomOperation;
    const hasEntite = !!activeFilters.entite;
    const hasCreateur = !!activeFilters.createur;
    const hasStatus = !!activeFilters.status;
    const hasType = !!(activeFilters.type && activeFilters.type !== 'Tous');
    const hasPrixMin = !!(activeFilters.prixRange && activeFilters.prixRange.min > 0);
    const hasPrixMax = !!(activeFilters.prixRange && activeFilters.prixRange.max > 0);
    const hasDateStart = !!(activeFilters.dateRange && activeFilters.dateRange.start);
    const hasDateEnd = !!(activeFilters.dateRange && activeFilters.dateRange.end);

    return (
      hasLotTechnique || hasLotGeographique || hasNomOperation || hasEntite ||
      hasCreateur || hasStatus || hasType || hasPrixMin || hasPrixMax ||
      hasDateStart || hasDateEnd
    );
  }, [activeFilters]);

  /**
   * Applique les filtres en appelant l'API
   */
  const handleFilterApply = async (filters: FilterOptions) => {
    // Fermer le modal immédiatement
    setIsFilterModalOpen(false);

    // Sauvegarder les filtres actifs
    setActiveFilters(filters);

    // Réinitialiser la page à 1 lors de l'application de nouveaux filtres
    setCurrentPage(1);

    // Charger les données avec les nouveaux filtres selon l'onglet actif
    if (TABS[activeTab] === 'Paniers importés') {
      await fetchCommandesImporteesWithFilters(1, filters);
    } else if (TABS[activeTab] === 'Archive') {
      await fetchArchives({ page: 1 });
    } else if (TABS[activeTab] === 'Chiffrages') {
      await fetchChiffrages();
    } else {
      await fetchCommandesWithFilters(1, activeTab, filters);
    }
  };

  /**
   * Réinitialise tous les filtres et recharge les données
   */
  const handleClearFilters = async () => {
    setActiveFilters({});
    setCurrentPage(1);

    if (TABS[activeTab] === 'Paniers importés') {
      await fetchCommandesImporteesWithFilters(1, {});
    } else if (TABS[activeTab] === 'Archive') {
      await fetchArchives({ page: 1 });
    } else if (TABS[activeTab] === 'Chiffrages') {
      await fetchChiffrages();
    } else {
      await fetchCommandesWithFilters(1, activeTab, {});
    }
  };

  // ============================================================================
  // NOTIFICATIONS
  // ============================================================================

  const getActionRequiseCount = () => {
    return commandes.filter(cmd => cmd.status.action === true && cmd.source !== "BCI").length;
  };

  const notifications = [
    {
      tabIndex: TABS.indexOf('Tous'),
      count: actionRequisCount
    },
    {
      tabIndex: TABS.indexOf('Action requise'),
      count: actionRequisCount
    },
    {
      tabIndex: TABS.indexOf('Paniers importés'),
      count: paniersImportesCount
    }
  ].filter(n => n.tabIndex !== -1 && n.count > 0);

  // ============================================================================
  // COLONNES TABLEAU - ARCHIVES
  // ============================================================================

  const archiveColumns = useMemo(() => [
    {
      field: 'type',
      header: 'Type',
      renderCell: () => <div className="type-info">BC</div>
    },
    {
      field: 'date_commande',
      header: 'Date',
      renderCell: (value: any) => <div>{formatDate(value, false)}</div>
    },
    {
      field: 'panier',
      header: 'Nom',
      renderCell: (value: string, row: any) => (
        <div className="commande-nom text-truncate" title={row.panier || '-'}>{row.panier || '-'}</div>
      )
    },
    {
      field: 'organisme',
      header: isClientView ? 'Prestataire' : 'Client',
      renderCell: (value: any, row: any) => {
        const displayValue = row.organisme?.nom || (isClientView ? '-' : '-');
        return <div className="client-info text-truncate" title={displayValue}>{displayValue}</div>;
      }
    },
    {
      field: 'total_ht',
      header: 'Total HT',
      renderCell: (value: number, row: any) => (
        <div className="montant-commande">
          {formatPrice(Number(row?.total_ht))}
        </div>
      )
    },
    {
      field: 'statut',
      header: 'Statut',
      // Meme table de libelles que la liste acheteur et le SuiviHeader (utils/statusLabel) : cette
      // colonne affichait le nom BRUT de la base ("Validé" pour un BC accepte). La classe CSS reste
      // derivee du nom brut, pour ne pas changer les couleurs de pastille.
      renderCell: (value: string, row: any) => {
        const libelle = libelleStatut(row?.source, row?.status?.id, value);
        return (
          <div className={`status-badge ${toCssClass(value)}`}>
            {libelle ? libelle.charAt(0).toUpperCase() + libelle.slice(1) : '-'}
          </div>
        );
      }
    },
    {
      field: 'actions',
      header: 'Action',
      renderCell: (value: any, row: any) => (
        <Button
          value="PDF"
          variant="secondary"
          size="small"
          className="action-button"
          onClick={() => downloadArchivePDF(row.id)}
        />
      )
    }
  ], [isClientView]);

  // Helper pour exécuter les actions (déplacé avant columns pour éviter les problèmes de référence)
  const executeAction = useCallback((action: string, row: Commande) => {
    switch (action) {
      case 'reprendre': handleReprendre(row.id.toString()); break;
      case 'analyser': handleAnalyser(row.id.toString()); break;
      case 'candidater': handleCandidater(row.id.toString()); break;
      case 'signer': handleSigner(row.id.toString()); break;
      case 'export': handleExport(row.id.toString()); break;
      case 'download': handleDownload(row); break;
      case 'accepter': handleAccepter(row.id.toString()); break;
      case 'chiffrer': handleChiffrer(row.id.toString()); break;
      case 'valider': handleValider(row); break;
      case 'view':
        if (row.source === 'BCI') {
          navigate(`/commande-bci/${row.id}?tab=0`, { state: { action: 'view' } });
        }
        break;
      default: console.log(`Aucune action définie pour: ${action}`);
    }
  }, [navigate, handleReprendre, handleAnalyser, handleCandidater, handleSigner, handleExport, handleDownload, handleAccepter, handleChiffrer, handleValider]);

  // ============================================================================
  // COLONNES TABLEAU - COMMANDES
  // ============================================================================

  const columns = useMemo(() => [
    {
      field: 'operation',
      header: '',
      width: '48px',
      renderCell: (_value: any, row: any) => {
        const opId = row.operation?.id;
        const src = row.source ?? row.type;
        const counts = opId ? unreadMsgCounts[`${src}:${opId}`] : undefined;
        const total = (counts?.questions_reponses ?? 0) + (counts?.messagerie ?? 0);
        if (total === 0) return null;
        return (
          <span className="msg-unread-badge" title={`${total} message(s) non lu(s)`}>
            {total}
          </span>
        );
      }
    },
    {
      field: 'id',
      header: 'N°',
      renderCell: (value: number, row: Commande) => {
        const displayValue = row.operation?.numero || value.toString();
        return <div className="date-info text-truncate" title={displayValue}>{displayValue}</div>;
      }
    },
    {
      field: 'source',
      header: 'Type',
      renderCell: (value: string) => (
        <div className="type-info text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'created_at',
      header: 'Date',
      renderCell: (value: string) => {
        const date = new Date(value);
        const formattedDate = date.toLocaleDateString('fr-FR');
        return <div className="date-info text-truncate" title={formattedDate}>{formattedDate}</div>;
      }
    },
    {
      field: 'lot_technique',
      header: 'Lot Technique',
      renderCell: (value: any, row: any) => {
        const lotTechniqueName = value?.nom || '-';
        return <div className="lot-technique text-truncate" title={lotTechniqueName}>{row.operation?.id_lot_technique}</div>;
      }
    },
    {
      field: 'id_lot_departement',
      header: 'Dép',
      renderCell: (value: number) => (
        <div className="lot-geographique text-truncate" title={value?.toString()}>{value?.toString()}</div>
      )
    },
    {
      field: 'cree_par',
      header: 'Créé par',
      renderCell: (_value: string, row: Commande) => {
        const creePar = row?.operation?.cree_par || '-';
        return <div className="user-info text-truncate" title={creePar}>{creePar}</div>;
      }
    },
    {
      field: 'nom',
      header: 'Nom',
      renderCell: (value: string) => (
        <div className="commande-nom text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'organisme_nom',
      header: isClientView ? 'Prestataire' : 'Client',
      renderCell: (value: string) => {
        let displayValue: string;
        if (value === 'Non défini' || !value) {
          displayValue = isClientView ? 'Prestataire non défini' : 'Client non défini';
        } else {
          displayValue = value;
        }
        return <div className="client-info text-truncate" title={displayValue}>{displayValue}</div>;
      }
    },
    {
      field: 'prix_total',
      header: 'Total HT',
      renderCell: (value: number | any | null) => (
        <div className="montant-commande">
          {value !== undefined && value !== null ? formatPrice(parseFloat(value), 2) : '0,00 €'}
        </div>
      )
    },
    {
      field: 'status',
      header: 'Statut',
      renderCell: (value: { nom: string, id: number, delai: number | null, action?: string, type_compte: string }, row: Commande) => {
        if (!value) return null;
        const statusClass = toCssClass(value?.nom);
        const actionClass = value?.action ? 'action-requise' : '';
        let displayNom = value?.nom;

        if (row?.source === 'MS') {
          if (value?.id === 6) displayNom = 'Refusé';
          if (value?.id === 11) displayNom = 'Accepté';
          if (value?.id === 12 && row?.expire_at) displayNom = 'Expiré';
        }

        if (row?.source === 'BC') {
          if (value?.id === 8) displayNom = 'Refusé';
          if (value?.id === 7) displayNom = 'Accepté';
        }

        return (
          <div className={`status-badge ${statusClass} ${actionClass}`} title={`Type: ${value?.type_compte}`}>
            {displayNom}
          </div>
        );
      }
    },
    {
      field: 'expire_at',
      header: 'Temps restant',
      renderCell: (value: string, row: Commande) => {
        if (!row) return null;

        // Pour les paniers importés (BCI), afficher la date de début d'opération
        if (row.source === 'BCI' && row.operation?.date_debut_operation) {
          const date = new Date(row.operation.date_debut_operation);
          const formattedDate = date.toLocaleDateString('fr-FR');
          return (
            <div className="date-info" title={formattedDate}>
              {formattedDate}
            </div>
          );
        }

        // Pour les autres (BC/MS), afficher le compte à rebours.
        // MS : sur une COMMANDE presta, le délai vivant n'existe qu'au statut 3 « À candidater »
        // (l'acheteur a lancé l'analyse, le presta doit déposer son offre avant expire_at). Une
        // fois l'offre déposée, la commande passe à 5 « En attente d'analyse » -> lecture seule,
        // plus de chrono. Le statut 2 « Consultation en cours » est porté par l'OPÉRATION, jamais
        // par une commande -> l'ancien test `=== 2` n'était jamais vrai, d'où l'absence de chrono.
        // Au-delà (refusé=6, expiré=12...), expire_at est résiduel -> pas de chrono (sinon
        // « Expiré » à tort). Cf. StatusId dans SuiviDeCommandeMSV2/types.ts.
        // BC : comportement existant conservé (exclusion des statuts terminés/refusés/expirés).
        if (row?.expire_at) {
          const show = row.source === 'MS'
            ? row?.status?.id === 3
            : ![5, 6, 7, 8, 12].includes(row?.status?.id);
          if (show) {
            return <CompteARebourd targetDate={row?.expire_at} />;
          }
        }
        return null;
      }
    },
    {
      field: 'actions',
      header: 'Action',
      renderCell: (value: any, row: Commande) => {
        if (!row || !row?.status) return null;

        const userRole = getUserRoleFromStorage();
        const actionMapping = getStatusActionMapping(row?.status?.id, userRole, row);

        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {actionMapping?.showButton && (
              <Button
                value={actionMapping.buttonText}
                variant="secondary"
                size="small"
                onClick={() => executeAction(actionMapping.action, row)}
                className={`action-button ${toCssClass(row.status.nom)} ${row.status.action ? 'action-requise' : ''} ${actionMapping.action === 'download' ? 'action-button--pdf' : ''}`}
                disabled={actionMapping.disableButton}
              />
            )}
            {row?.source === 'BCI' && row?.status?.id === 11 && (
              <Button
                variant="ghost"
                value="×"
                title="Supprimer la commande"
                onClick={(e) => {
                  e?.stopPropagation();
                  handleDeleteBCI(row);
                }}
              />
            )}
          </div>
        );
      }
    }
  ], [isClientView, executeAction, handleDeleteBCI, unreadMsgCounts]);

  // ============================================================================
  // CHIFFRAGES (onglet "Chiffrage")
  // ============================================================================

  // Index des couples (lot technique x département x rang) où le prestataire est titulaire. Sert à
  // enrichir l'affichage (code/nom du lot, nom du département) et à retrouver l'id_lot_contrat lors
  // de la génération du devis PDF (les prix ne sont pas stockés, ils se résolvent via le catalogue).
  const coupleByKey = useMemo(() => {
    const map = new Map<string, ChiffrageCouple>();
    chiffrageCouples.forEach((c) => {
      map.set(`${c.id_lot_technique}:${c.id_departement}:${c.rang}`, c);
    });
    return map;
  }, [chiffrageCouples]);

  // Liste affichée : on retire les chiffrages masqués (le prestataire les a sortis de sa liste ; le
  // code reste valide côté acheteur). Plus de filtre par statut : l'état affiché se déduit du code.
  //
  // La recherche s'applique ici en local : contrairement aux autres onglets, cette liste
  // n'est pas paginée côté serveur (elle est servie entière par chiffrageService), donc
  // rien à demander à l'API. Sans ce filtre, la barre de recherche resterait inerte sur
  // cet onglet alors qu'elle est visible.
  const filteredChiffrages = useMemo(() => {
    const visibles = chiffrages.filter((ch) => !ch.masque_at);

    if (!searchQuery) {
      return visibles;
    }

    const terme = searchQuery.toLowerCase();
    return visibles.filter((ch) =>
      (ch.libelle || '').toLowerCase().includes(terme) ||
      (ch.code || '').toLowerCase().includes(terme)
    );
  }, [chiffrages, searchQuery]);

  // Copie le code dans le presse-papiers avec un feedback transitoire sur le bouton.
  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      window.setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      // Presse-papiers indisponible : on n'interrompt pas le parcours (le code reste lisible en colonne).
    }
  };

  // Retire le chiffrage de la liste du prestataire (le statut reste "actif", le code reste valide).
  // Suppression d'un chiffrage : double confirmation. 1) la corbeille arme la modale (aucun appel
  // reseau), 2) la modale confirme. Le back n'autorise la suppression que sur un brouillon.
  const askDeleteChiffrage = (row: Chiffrage) => {
    setChiffrageToDelete(row);
    setDeleteChiffrageError(null);
  };

  const cancelDeleteChiffrage = () => {
    setChiffrageToDelete(null);
    setDeleteChiffrageError(null);
    setIsDeletingChiffrage(false);
  };

  // Une seule action cote presta : "Supprimer". Sa mecanique depend de l'existence d'un CODE :
  //  - pas de code  -> suppression reelle (aucun code ne circule, le back n'autorise que ce cas) ;
  //  - code genere  -> le chiffrage sort de la liste du presta, mais le CODE RESTE VALIDE pour les
  //    acheteurs (regle metier : un code communique ne peut pas etre revoque). C'est le masquage,
  //    exposé sous le meme libelle pour ne pas imposer deux notions au prestataire.
  const confirmDeleteChiffrage = async () => {
    if (!chiffrageToDelete) return;
    setIsDeletingChiffrage(true);
    setDeleteChiffrageError(null);
    try {
      if (chiffrageToDelete.code) {
        await chiffrageService.hideChiffrage(chiffrageToDelete.id);
      } else {
        await chiffrageService.deleteChiffrage(chiffrageToDelete.id);
      }
      setChiffrageToDelete(null);
      await fetchChiffrages();
    } catch (error: any) {
      // On garde la modale ouverte et on y affiche l'erreur (plutot qu'une alert qui perd le contexte).
      setDeleteChiffrageError(error?.message || 'Une erreur est survenue lors de la suppression du chiffrage.');
    } finally {
      setIsDeletingChiffrage(false);
    }
  };

  // Prix total d'une liste de chiffrages, resolu a la volee : aucun prix n'est stocke (les prix suivent
  // l'accord-cadre). On resout donc le catalogue du titulaire, MIS EN CACHE par id_lot_contrat : un
  // presta a peu de couples, on fait donc quelques appels et non un par ligne.
  const computeChiffrageTotals = async (list: Chiffrage[], couples: ChiffrageCouple[]) => {
    const byKey = new Map<string, ChiffrageCouple>();
    couples.forEach((c) => byKey.set(`${c.id_lot_technique}:${c.id_departement}:${c.rang}`, c));

    const catalogueCache = new Map<number, Article[]>();
    const totals = new Map<number, number>();

    for (const ch of list) {
      const lignes = ch.articles || [];
      const couple = byKey.get(`${ch.id_lot_technique}:${ch.id_departement}:${ch.rang}`);
      if (!couple || lignes.length === 0) {
        totals.set(ch.id, 0);
        continue;
      }
      let catalogue = catalogueCache.get(couple.id_lot_contrat);
      if (!catalogue) {
        catalogue = await ArticlesService.getArticlesByCommandes([
          {
            id_contrat: couple.id_lot_contrat,
            id_lot_departement: ch.id_departement,
            id_lot_technique: ch.id_lot_technique,
          },
        ]).catch(() => [] as Article[]);
        catalogueCache.set(couple.id_lot_contrat, catalogue);
      }
      const byId = new Map<string, Article>();
      catalogue.forEach((a) => byId.set(String(a.id), a));

      const total = lignes.reduce((sum, ligne) => {
        const art = byId.get(String(ligne.id_catalogue));
        const prixUnitaire = art ? Number(ArticlesService.getPrixByQuantity(art, ligne.quantite)) || 0 : 0;
        return sum + prixUnitaire * ligne.quantite;
      }, 0);
      totals.set(ch.id, total);
    }

    setChiffrageTotals(totals);
  };

  // Génère et télécharge le devis PDF d'un chiffrage. La ligne de liste ne porte que le nombre
  // d'articles : on recharge le détail pour ses lignes, puis on résout les prix via le catalogue du
  // couple titulaire (id_lot_contrat). En cas d'échec de résolution, le devis reste généré à 0.
  const handleDownloadChiffragePDF = async (row: Chiffrage) => {
    try {
      const detail = await chiffrageService.getChiffrage(row.id);
      const lignes = detail.articles || [];

      const couple = coupleByKey.get(`${row.id_lot_technique}:${row.id_departement}:${row.rang}`);
      let catalogue: Article[] = [];
      if (couple) {
        catalogue = await ArticlesService.getArticlesByCommandes([{
          id_contrat: couple.id_lot_contrat,
          id_lot_departement: row.id_departement,
          id_lot_technique: row.id_lot_technique,
        }]).catch(() => [] as Article[]);
      }
      const catalogueById = new Map<string, Article>();
      catalogue.forEach((a) => catalogueById.set(String(a.id), a));

      const pdfArticles: ChiffragePDFArticle[] = lignes.map((ligne) => {
        const art = catalogueById.get(String(ligne.id_catalogue));
        const prixUnitaire = art ? (Number(ArticlesService.getPrixByQuantity(art, ligne.quantite)) || 0) : 0;
        return {
          code: art?.code || String(ligne.id_catalogue),
          nom: art?.nom || '-',
          quantite: ligne.quantite,
          prixUnitaire,
          prixTotal: prixUnitaire * ligne.quantite,
          unite: art?.unite || '-',
        };
      });

      const total = pdfArticles.reduce((sum, a) => sum + a.prixTotal, 0);
      const entete: ChiffragePDFEntete = { nom: user?.name };
      const code = detail.code || row.code || '';

      await handlePrint(
        <ChiffragePDF chiffrage={detail} articles={pdfArticles} entete={entete} total={total} code={code} />,
        `devis-chiffrage-${code || row.id}.pdf`,
      );
    } catch (error: any) {
      console.error('Erreur lors de la génération du devis:', error);
      alert(error?.message || 'Une erreur est survenue lors de la génération du devis.');
    }
  };

  // Colonnes de l'onglet "Chiffrage" (même forme que archiveColumns : field/header/renderCell).
  // Largeurs explicites : sans elles, les colonnes se repartissaient au gre du contenu et ne
  // tombaient plus en face de leurs entetes. Les classes sont propres a ce tableau (celles du tableau
  // commandes, recopiees, portaient une mise en forme pensee pour d'autres colonnes).
  const chiffragesColumns = useMemo(() => [
    {
      // Date de CREATION (et non code_genere_at, null tant qu'aucun code n'est genere).
      field: 'created_at',
      header: 'Date',
      width: '110px',
      renderCell: (value: string | null) => (
        <div className="date-info">{value ? formatDate(value, false) : '-'}</div>
      )
    },
    {
      field: 'libelle',
      header: 'Libellé',
      width: '220px',
      renderCell: (_value: string | null, row: Chiffrage) => (
        <div className="commande-nom text-truncate" title={row.libelle || '-'}>{row.libelle || '-'}</div>
      )
    },
    {
      field: 'id_lot_technique',
      header: 'Lot technique',
      width: '130px',
      renderCell: (value: number, row: Chiffrage) => {
        const couple = coupleByKey.get(`${row.id_lot_technique}:${row.id_departement}:${row.rang}`);
        const label = couple?.lot_code || (value != null ? value.toString() : '-');
        return <div className="text-truncate" title={couple?.lot_nom || label}>{label}</div>;
      }
    },
    {
      field: 'id_departement',
      header: 'Département',
      width: '150px',
      renderCell: (value: number, row: Chiffrage) => {
        const couple = coupleByKey.get(`${row.id_lot_technique}:${row.id_departement}:${row.rang}`);
        const label = couple?.departement_nom || (value != null ? value.toString() : '-');
        return <div className="text-truncate" title={label}>{label}</div>;
      }
    },
    {
      field: 'rang',
      header: 'Rang',
      width: '70px',
      renderCell: (value: number) => (
        <div>{value != null ? value.toString() : '-'}</div>
      )
    },
    {
      // "État" dérivé du code (plus du statut back) : le prestataire ne voit qu'un état binaire et
      // immuable — code émis => "Code généré", sinon => "En cours". On conserve field:'statut' pour
      // ne pas dupliquer la clé de la colonne "Code", mais l'affichage ne s'appuie plus sur row.statut.
      field: 'statut',
      header: 'État',
      width: '120px',
      renderCell: (_value: string, row: Chiffrage) => {
        const etat = row.code ? 'Code généré' : 'En cours';
        return <div className={`status-badge ${toCssClass(etat)}`}>{etat}</div>;
      }
    },
    {
      field: 'code',
      header: 'Code',
      width: '180px',
      // Le code porte son propre bouton de copie (icone seule, intention donnee au survol) : plus de
      // bouton "Copier le code" dans la colonne Action.
      renderCell: (value: string | null, row: Chiffrage) => (
        <div className="chiffrage-code-cell">
          <span className="text-truncate chiffrage-code" title={value || '-'}>{value || '-'}</span>
          {value && (() => {
            // Accuse de reception : l'icone passe temporairement au "check" (2s, pilote par
            // `copiedCode` dans handleCopyCode), l'infobulle suit, ET un texte le dit explicitement
            // (une icone seule ne confirme rien de lisible).
            const justCopied = copiedCode === value;
            const Icone = justCopied ? CheckIcon : CopyIcon;
            return (
              <>
                <Button
                  variant="ghost"
                  value=""
                  size="small"
                  className="chiffrage-code-copy"
                  icon={Icone ? <Icone /> : null}
                  title={justCopied ? 'Code copié' : 'Copier le code'}
                  onClick={(e) => {
                    e?.stopPropagation(); // ne pas ouvrir le chiffrage en cliquant sur la copie
                    handleCopyCode(value);
                  }}
                />
                {justCopied && <span className="chiffrage-code-copie">Copié</span>}
              </>
            );
          })()}
        </div>
      )
    },
    {
      // Prix total resolu a la volee (aucun prix stocke) : vide tant que la resolution n'a pas rendu.
      field: 'prix_total',
      header: 'Prix total',
      width: '120px',
      renderCell: (_value: any, row: Chiffrage) => {
        const total = chiffrageTotals.get(row.id);
        // Meme formateur que les autres colonnes "Total HT" de ce tableau (entityUtils.formatPrice).
        return <div className="total-ht">{total != null ? formatPrice(total, 2) : '-'}</div>;
      }
    },
    {
      field: 'actions',
      header: 'Action',
      width: '260px',
      renderCell: (_value: any, row: Chiffrage) => {
        // Pas de bouton "Voir" : la ligne entiere est cliquable (cf. handleRowClick).
        const isActif = row.statut === 'actif' && !!row.code;
        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            {isActif && (
              <Button
                value="Devis PDF"
                variant="secondary"
                size="small"
                className="action-button"
                onClick={(e) => {
                  e?.stopPropagation(); // ne pas ouvrir le chiffrage en cliquant sur le devis
                  handleDownloadChiffragePDF(row);
                }}
              />
            )}
            {/* Corbeille TOUJOURS affichee (meme presentation que la suppression d'un brouillon
                d'operation acheteur). Une seule action pour le presta : "Supprimer". Elle ARME la
                modale de confirmation, elle ne supprime pas directement. */}
            <Button
              variant="cancel"
              value=""
              size="small"
              className="delete-member-button"
              icon={DeleteIcon ? <DeleteIcon /> : null}
              title="Supprimer ce chiffrage"
              onClick={(e) => {
                e?.stopPropagation(); // ne pas declencher le clic de ligne
                askDeleteChiffrage(row);
              }}
            />
          </div>
        );
      }
    }
  ], [coupleByKey, copiedCode, chiffrageTotals]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calcul du prix maximum pour les filtres
  const maxPrice = useMemo(() =>
    Math.max(...commandes.map(cmd => cmd.prix_total || 0))
    , [commandes]);

  // ============================================================================
  // FETCH FONCTIONS
  // ============================================================================

  const fetchArchives = async (params: { page?: number } = {}) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const queryParams = {
        ...params,
        per_page: '100',
        // Les archives sont servies par un contrôleur distinct (bdc-archive), dont le
        // seul filtre texte est le numéro de marché. La barre de recherche y reste donc
        // active, mais avec cette portée-là.
        ...(searchQuery && { numero_marche: searchQuery }),
        ...(params.page !== undefined && { page: params.page.toString() })
      };
      const query = new URLSearchParams(queryParams as Record<string, string>).toString();

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bdc-archive?${query}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          signal: controller.signal,
        }
      );

      if (controller.signal.aborted) return;

      const result = await response.json();

      if (result.status === 'success') {
        setDataArchive(result.data || []);
        setPagination({
          currentPage: result.pagination.current_page,
          lastPage: result.pagination.last_page,
          perPage: result.pagination.per_page,
          total: result.pagination.total
        });
      } else {
        console.error('Erreur API Archives:', result);
        setDataArchive([]);
      }
    } catch (error: any) {
      if (error.name === "AbortError") {
        console.log("Requête archives annulée");
        return;
      }
      console.error('Erreur réseau Archives:', error);
      setDataArchive([]);
    }
  };

  /**
   * Récupère les chiffrages du prestataire (tous statuts) + les couples titulaires. Liste servie
   * côté front (pas de pagination serveur) : le service ne prend pas de signal d'annulation, on se
   * protège des états périmés via le garde `controller.signal.aborted` après l'await.
   */
  const fetchChiffrages = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setError(null);

    try {
      const [list, couples] = await Promise.all([
        chiffrageService.listChiffrages(),
        chiffrageService.getCouples().catch(() => [] as ChiffrageCouple[]),
      ]);

      if (controller.signal.aborted) return;

      setChiffrages(list);
      setChiffrageCouples(couples);
      setPagination({
        currentPage: 1,
        lastPage: 1,
        perPage: 100,
        total: list.length,
      });

      // Totaux resolus apres l'affichage de la liste : la colonne se remplit ensuite, on ne retarde
      // pas le rendu du tableau pour des prix.
      computeChiffrageTotals(list, couples).catch(() => setChiffrageTotals(new Map()));
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        return;
      }
      console.error('Erreur lors du chargement des chiffrages:', error);
      setError(error?.message || 'Une erreur est survenue');
      setChiffrages([]);
    }
  };

  /**
   * Récupère les commandes avec support des filtres API
   */
  const fetchCommandesWithFilters = async (
    page = 1,
    tab = activeTab,
    filters: Partial<FilterOptions> = {}
  ) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error("Aucun token d'authentification trouvé");

      // Construire les paramètres API à partir des filtres UI
      const apiFilters = transformFiltersToApiParams(filters, page, 100);

      // La recherche se cumule aux filtres de la popup et au filtre d'onglet (ET),
      // au lieu de les remplacer comme le faisait l'appel direct précédent.
      if (searchQuery) {
        apiFilters.search = searchQuery;
      }

      // Construction des paramètres de base avec le mapping des onglets
      let baseQueryParams = `page=${page}&per_page=100`;

      if (tab !== 0 && tab >= 0 && tab < TABS.length) {
        const tabMapping = TAB_TO_STATUS_MAPPING[tab];
        if (tabMapping && tabMapping.filter) {
          baseQueryParams = `${tabMapping.filter}&page=${page}&per_page=100`;
        }
      }

      if (selectedType !== 'Tous') {
        baseQueryParams += `&type=${selectedType}`;
      }

      // Fusionner les paramètres de base avec les filtres
      const finalQueryString = mergeFiltersWithBaseParams(baseQueryParams, apiFilters);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/commandes?${finalQueryString}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          signal: controller.signal
        }
      );

      if (controller.signal.aborted) return;

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Erreur HTTP ${response.status}`);
      }

      const responseData: ApiResponse = await response.json();

      if (responseData.status !== 'success' || !Array.isArray(responseData.data)) {
        throw new Error('Format de réponse API inattendu');
      }

      // Transformation des données avec la fonction utilitaire
      const processedData = transformCommandesData(responseData.data);

      // ✅ SUPPRIMÉ : Filtrage local des dates (maintenant géré côté backend)
      // ✅ SUPPRIMÉ : Filtrage local du créateur (maintenant géré côté backend)
      // ✅ SUPPRIMÉ : Filtrage local de l'entité (maintenant géré côté backend)

      setCommandes(processedData);
      setFilteredData(processedData);
      setPagination({
        currentPage: responseData.pagination?.current_page || 1,
        lastPage: responseData.pagination?.last_page || 1,
        perPage: responseData.pagination?.per_page || 50,
        total: responseData.pagination?.total || 0
      });

      // Charger les badges messagerie (MS + BDC) en batch, keyes par `${source}:${operationId}`.
      const auteurType = isClientView ? 'acheteur' : 'prestataire';
      const msIds = processedData
        .filter((cmd: any) => cmd.source === 'MS' || cmd.type === 'MS')
        .map((cmd: any) => cmd.operation?.id).filter(Boolean);
      const bcIds = processedData
        .filter((cmd: any) => cmd.source === 'BC')
        .map((cmd: any) => cmd.operation?.id).filter(Boolean);
      // Q&R MS prestataire : timestamps de derniere lecture + id_entite. Sans objet en BDC (pas de Q&R).
      const msSince = !isClientView
        ? msIds.map((id: number) => {
          const lastRead = localStorage.getItem(`qr_last_read_${id}`);
          return lastRead ? `since[${id}]=${encodeURIComponent(lastRead)}` : null;
        }).filter(Boolean).join('&')
        : '';
      const msExtra = [
        !isClientView && user?.entiteId ? `id_entite=${user.entiteId}` : '',
        msSince,
      ].filter(Boolean).join('&');
      try {
        const [msMap, bcMap] = await Promise.all([
          fetchUnreadCountsMap('/subsequents', auteurType, msIds, msExtra),
          fetchUnreadCountsMap('/bdc', auteurType, bcIds),
        ]);
        const merged: Record<string, { questions_reponses: number; messagerie: number }> = {};
        Object.entries(msMap).forEach(([id, c]) => { merged[`MS:${id}`] = c; });
        Object.entries(bcMap).forEach(([id, c]) => { merged[`BC:${id}`] = c; });
        setUnreadMsgCounts(merged);
      } catch {
        // Silencieux : les badges ne sont pas critiques
      }

    } catch (error: any) {
      if (error.name === "AbortError") {
        console.log("Requête commandes annulée");
        return;
      }
      console.error('Erreur lors du chargement des commandes:', error);
      setError(error.message || 'Une erreur est survenue');
      setCommandes([]);
      setFilteredData([]);
    }
  };

  /**
   * Fonction simplifiée pour charger les commandes sans filtres personnalisés
   */
  const fetchCommandes = async (page = 1, tab = activeTab) => {
    await fetchCommandesWithFilters(page, tab, activeFilters);
  };

  /**
   * Récupère les commandes importées (BCI) avec support des filtres API
   */
  const fetchCommandesImporteesWithFilters = async (
    page = 1,
    filters: Partial<FilterOptions> = {}
  ) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error("Aucun token d'authentification trouvé");

      // Construire les paramètres API à partir des filtres UI
      const apiFilters = transformFiltersToApiParams(filters, page, 100);

      if (searchQuery) {
        apiFilters.search = searchQuery;
      }

      const queryString = buildQueryString(apiFilters);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/commandes-importees?${queryString}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          signal: controller.signal
        }
      );

      if (controller.signal.aborted) return;

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Erreur HTTP ${response.status}`);
      }

      const responseData = await response.json();

      if (responseData.status !== 'success' || !Array.isArray(responseData.data)) {
        throw new Error('Format de réponse API inattendu');
      }

      // Transformation et tri des paniers importés
      let processedData = transformCommandesData(responseData.data);

      // Appliquer les filtres locaux pour les champs non supportés par l'API backend
      if (filters.dateRange?.start && filters.dateRange?.end) {
        processedData = processedData.filter(cmd => {
          const cmdDate = new Date(cmd.created_at);
          return cmdDate >= filters.dateRange!.start! && cmdDate <= filters.dateRange!.end!;
        });
      }

      if (filters.createur) {
        processedData = processedData.filter(cmd =>
          cmd.operation?.cree_par?.toLowerCase().includes(filters.createur!.toLowerCase()) ||
          cmd.cree_par?.toLowerCase().includes(filters.createur!.toLowerCase())
        );
      }

      if (filters.entite) {
        processedData = processedData.filter(cmd =>
          cmd.organisme_nom === filters.entite ||
          cmd.operation?.organisme_nom === filters.entite
        );
      }

      const sortedData = sortPaniersImportesByPriority(processedData);

      setCommandes(sortedData);
      setFilteredData(sortedData);
      setPagination({
        currentPage: responseData.pagination?.current_page || 1,
        lastPage: responseData.pagination?.last_page || 1,
        perPage: responseData.pagination?.per_page || 100,
        total: responseData.pagination?.total || 0
      });

    } catch (error: any) {
      if (error.name === "AbortError") {
        console.log("Requête paniers importés annulée");
        return;
      }
      console.error('Erreur lors du chargement des paniers importés:', error);
      setError(error.message || 'Une erreur est survenue');
      setCommandes([]);
      setFilteredData([]);
    }
  };

  /**
   * Fonction simplifiée pour charger les commandes importées sans filtres
   */
  const fetchCommandesImportees = async (page = 1, tab = activeTab) => {
    await fetchCommandesImporteesWithFilters(page, activeFilters);
  };

  // Fonction unique pour charger les compteurs (Action requise + Paniers importés)
  const fetchCounts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/commandes-compteur`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );

      if (!response.ok) return;

      const result = await response.json();
      if (result.status === 'success' && result.data) {
        const count = result.data.count ?? 0;        // MS + BDC pour "Action requise"
        const countBDCI = result.data.count_bdci ?? 0; // BCI pour "Paniers importés"
        const totalCount = count + countBDCI;         // Total pour le badge menu

        setActionRequisCount(count);
        setPaniersImportesCount(countBDCI);
        setUnreadCommandes(totalCount);
        if (onNotificationUpdate) {
          onNotificationUpdate(totalCount);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des compteurs:', error);
    }
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Effet pour charger les compteurs une seule fois au montage
  useEffect(() => {
    fetchCounts();
  }, []);

  // Effet principal - chargement des données selon l'onglet actif
  useEffect(() => {
    let isCancelled = false;

    const loadData = async () => {
      setLoading(true);

      try {
        if (TABS[activeTab] === 'Archive') {
          await fetchArchives({ page: currentPage });
        } else if (TABS[activeTab] === 'Chiffrages') {
          await fetchChiffrages();
        } else if (TABS[activeTab] === 'Paniers importés') {
          await fetchCommandesImportees(currentPage, activeTab);
        } else {
          await fetchCommandes(currentPage, activeTab);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
          // La barre de recherche déclenche cet effet : c'est lui qui sait quand le
          // rechargement est réellement terminé, quel que soit l'onglet servi.
          setSearchLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isCancelled = true;
      abortControllerRef.current?.abort();
    };
  }, [activeTab, selectedType, currentPage, searchQuery]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="commandes-page">
      <section className="tableau-de-bord-section">
        <TabNav
          tabs={TABS as unknown as string[]}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          notifications={notifications}
          inactiveTabs={[]}
          inactiveTabsTooltips={{
            "Paniers importés": "Nous procédons à l'importation de l'ancienne base de cours. Merci de votre patience : l'opération nécessite encore quelques heures."
          }}
          rightAlignedTabs={['Archive', 'Paniers importés']}
        />

        <div className="button-group">
          <div className="button-filter-group">
            <SearchBar
              onSearch={handleSearch}
              onClear={() => handleSearch('')}
              placeholder="Trouver une commande"
              isLoading={searchLoading}
            />
            <Button
              value="exporter"
              variant="secondary"
              size="small"
              icon={ExportIcon ? <ExportIcon /> : null}
              onClick={handleExportAll}
              disabled={isExporting}
            />
            <div className="filter-button-container">
              <Button
                value="filtrer"
                variant={hasActiveFilters ? "primary" : "secondary"}
                size="small"
                icon={FilterIcon ? <FilterIcon /> : null}
                onClick={handleFilter}
              />
              {hasActiveFilters && (
                <button
                  className="filter-reset-button"
                  onClick={handleClearFilters}
                  title="Réinitialiser les filtres"
                >
                  ×
                </button>
              )}
            </div>
          </div>
          <div className="commandes-actions">
            {TABS[activeTab] === 'Chiffrages' && (
              <Button
                value="Nouveau chiffrage"
                variant="primary"
                size="small"
                icon={PlusIcon ? <PlusIcon /> : null}
                onClick={() => setCreateChiffrageModalOpen(true)}
              />
            )}
          </div>
        </div>

        {loading ? (
          <TableSkeleton
            rows={8}
            columns={TABS[activeTab] === 'Archive' ? archiveColumns : TABS[activeTab] === 'Chiffrages' ? chiffragesColumns : columns}
          />
        ) : error ? (
          <div className="error-message">
            <p>Erreur: {error}</p>
          </div>
        ) : (
          <div className="tableau-commandes">
            <TableauDeBord
              data={TABS[activeTab] === 'Archive' ? dataArchive : TABS[activeTab] === 'Chiffrages' ? filteredChiffrages : filteredData}
              columns={TABS[activeTab] === 'Archive' ? archiveColumns : TABS[activeTab] === 'Chiffrages' ? chiffragesColumns : columns}
              onRowClick={handleRowClick}
              onSearch={handleSearch}
              typeFilters={true}
              onExport={() => handleExportCSV(filteredData, columns, `commandes-${new Date().toISOString().slice(0, 10)}.csv`)}
              isSelectable={true}
              showActionBar={true}
              selectedItems={selectedItems}
              onSelectionChange={handleSelectionChange}
              showActionsColumn={false}
              itemsPerPage={pagination.perPage}
              currentPage={pagination.currentPage}
              totalPages={pagination.lastPage}
              totalItems={pagination.total}
              onPageChange={handlePageChange}
              disableDefaultSort={TABS[activeTab] === 'Paniers importés'}
            />
          </div>
        )}
      </section>

      {/* Modal de filtres */}
      <FiltreDeCommande
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={handleFilterApply}
        data={commandes}
        maxPrice={maxPrice}
        activeFilters={activeFilters}
      />

      {/* Modal lancer marché */}
      <LancerMarcheModal
        isOpen={isLancerMarcheModalOpen}
        onClose={() => setIsLancerMarcheModalOpen(false)}
        title="Lancer un marché subséquent"
        showNomOperation={true}
        showLotTechnique={true}
        showLotGeographique={true}
        showDepartement={false}
        mode="MS"
      />

      {/* Modal Bon de Commande */}
      <LancerMarcheModal
        isOpen={isBonCommandeModalOpen}
        onClose={() => setIsBonCommandeModalOpen(false)}
        title="Nouveau Bon de Commande"
        showNomOperation={true}
        showLotTechnique={false}
        showLotGeographique={true}
        showDepartement={false}
        mode="BC"
        initialValues={bdcInitialValues}
      />

      {/* Modal "Nouveau chiffrage" : ouverte depuis l'onglet Chiffrages. La validation CREE le
          chiffrage (persiste, meme sans article) ; on ouvre ensuite la page sur ce chiffrage. */}
      <ChiffrageCreateModal
        isOpen={createChiffrageModalOpen}
        onClose={() => setCreateChiffrageModalOpen(false)}
        couples={chiffrageCouples}
        onCreated={(chiffrage) => {
          setCreateChiffrageModalOpen(false);
          navigate(`/chiffrage?id=${chiffrage.id}`);
        }}
      />

      {/* Double confirmation de suppression d'un chiffrage (meme patron que les brouillons
          d'operation acheteur). En cas d'echec, la modale reste ouverte et porte l'erreur. */}
      <ValidationPopUp
        isOpen={chiffrageToDelete !== null}
        title="Supprimer le chiffrage"
        text={
          deleteChiffrageError ||
          // Le message dit ce qui se passe reellement : un code deja communique reste utilisable par
          // l'acheteur, on ne laisse pas croire a une revocation.
          (chiffrageToDelete?.code
            ? `Êtes-vous sûr de vouloir supprimer le chiffrage${
                chiffrageToDelete?.libelle ? ` « ${chiffrageToDelete.libelle} »` : ''
              } ? Il sera retiré de votre liste. Le code ${chiffrageToDelete.code} que vous avez communiqué reste valide : les acheteurs peuvent continuer à l'utiliser.`
            : `Êtes-vous sûr de vouloir supprimer le chiffrage${
                chiffrageToDelete?.libelle ? ` « ${chiffrageToDelete.libelle} »` : ''
              } ? Cette action est irréversible.`)
        }
        onClose={cancelDeleteChiffrage}
        onConfirm={confirmDeleteChiffrage}
        cancelLabel="Annuler"
        confirmLabel="Supprimer"
        isLoading={isDeletingChiffrage}
      />

      {/* Modal de confirmation de suppression BCI */}
      <ValidationPopUp
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={deleteError ? handleCancelDelete : handleConfirmDelete}
        title="Supprimer le panier importé"
        text={deleteError || `Êtes-vous sûr de vouloir supprimer ce panier importé d'un montant de ${commandeToDelete ? formatPrice(commandeToDelete.prix_total || 0) : '0,00 €'} ? Cette action est irréversible.`}
        cancelLabel="Annuler"
        confirmLabel={deleteError ? "Fermer" : "Supprimer"}
        isLoading={isDeleting}
      />
    </div>
  );
}

export default Commandes;
